/**
 * @fileoverview A link to Code in Place, for use inside PAI's own prose.
 *
 * PAI's copy leans on Code in Place throughout — "taught the Code in Place
 * way", "the same model that has powered Code in Place since 2020" — but a PAI
 * visitor has no reason to know what that is, and nothing in PAI's chrome
 * takes them there: the shared footer's brand link is relabeled "PAI" and
 * points at PAI's own home. Routing every mention through this component makes
 * the phrase itself the way to find out.
 */

import type {ReactNode} from 'react';
import styled from 'styled-components';

/** The Code in Place site's front page. */
const CODE_IN_PLACE_URL = 'https://codeinplace.stanford.edu';

/** PAI's accent, matching the CARDINAL constant in the splash sections. */
const CARDINAL = '#8c1515';

/**
 * Renders the words "Code in Place" as a link to the Code in Place site,
 * opened in a new tab so a reader mid-application does not lose their place.
 *
 * @param children - Link text. Omitted renders "Code in Place"; pass something
 *   else only where the sentence needs different wording for the same link.
 * @return The rendered link.
 * @throws Never.
 */
export function CodeInPlaceLink({children}: {children?: ReactNode}) {
  return (
    <Anchor href={CODE_IN_PLACE_URL} target="_blank" rel="noreferrer">
      {children ?? 'Code in Place'}
    </Anchor>
  );
}

/*
 * Cardinal rather than the framework's link blue, so the link reads as part of
 * PAI's palette on every surface it appears on. Underlined by default because
 * these sit inside body prose, where colour alone is not enough to mark a link.
 */
const Anchor = styled.a`
  color: ${CARDINAL};
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${CARDINAL};
    text-decoration: none;
  }
`;
